import * as PIXI from "pixi.js";
import WebFont from "webfontloader";
import { Dictionary } from "../../util/collection/Dictionary";
import { Logger } from "../../util/log/Logger";
import { FontUtil } from "../../util/view/FontUtil";
import { FontProperties } from "../control/properties/FontProperties";
import { LanguageManager } from "./LanguageManager";
/**
 * FontMap.ts
 */
export class FontMap {
    constructor() {
        this.create();
        this.register();
        Logger.addObjectRef(this, "FontMap");
    }
    create() {
        this.fontManager = FontManager.getInstance();
        this.createFonts();
    }
    createFonts() {
    }
    register() {
    }
    addMapping(key, name, size, colour, uppercase = false) {
        let fontProperties = new FontProperties(name, size, colour, uppercase);
        this.fontManager.addMapping(key, fontProperties);
        return fontProperties;
    }
    addBitmapGenMapping(key, fontProperties, bitmapGenFontProperties) {
        const mappedFontProperties = typeof fontProperties === "string" ? this.fontManager.getMapping(fontProperties) : fontProperties;
        this.fontManager.addBitmapGenMapping(key, mappedFontProperties, bitmapGenFontProperties);
        return bitmapGenFontProperties;
    }
    getNativeFont(locale) {
        let text = new PIXI.Text(locale.getNativeMessage());
        let font = text.context.font;
        let fontParts = font.split(" ");
        let nativeFont = fontParts.length > 0 ? fontParts[1] : fontParts[0];
        Logger.forceLog("Native Font Being Used: " + nativeFont);
        return nativeFont;
    }
}
/**
 * FontManager.ts
 */
export class FontManager {
    constructor() {
        this.missing = [];
        if (FontManager.instance) {
            throw new Error("FontManager Singleton Already Initialised");
        }
        this.create();
        FontManager.instance = this;
    }
    create() {
        this.mappings = new Dictionary();
        this.bitmapGenMappings = new Dictionary();
        this.bitmapGenFonts = new Dictionary();
    }
    addMapping(key, value) {
        this.mappings.setValue(key, value);
    }
    addBitmapGenMapping(key, fontProperties, bitmapGenFontProperties) {
        const properties = bitmapGenFontProperties.clone();
        properties.fontProperties = fontProperties;
        this.bitmapGenMappings.setValue(key, properties);
    }
    getMapping(key) {
        let font;
        if (this.mappings.containsKey(key)) {
            font = this.mappings.getValue(key);
        }
        else {
            this.missing.push(key);
            Logger.verbose("Font mapping missing for key: " + key);
        }
        return font;
    }
    getWebFontNames(filter) {
        const fontNames = [];
        const properties = this.mappings.getValues();
        for (let value of properties) {
            if (!filter || filter(value)) {
                const name = value.font.family;
                if (fontNames.indexOf(name) == -1) {
                    fontNames.push(name);
                }
            }
        }
        return fontNames;
    }
    async loadWebFonts(version, cssPaths, loadedHandler) {
        const webFontNames = this.getWebFontNames();
        const numbersOnlyWebFontNames = this.getWebFontNames((value) => value.font.numbersOnly);
        let locale = LanguageManager.getInstance().getLocale();
        const scope = this;
        if (locale.useNativeFont() || webFontNames.length == 0) {
            if (numbersOnlyWebFontNames.length) {
                await this._loadWebFonts(cssPaths, numbersOnlyWebFontNames);
            }
        }
        else {
            await this._loadWebFonts(cssPaths, webFontNames);
        }
        this.bitmapGenMappings.forEach((key, properties) => this._addBitmapGenFont(key, properties));
        loadedHandler.call(scope, version);
    }
    _addBitmapGenFont(key, properties) {
        this.bitmapGenMappings.setValue(key, properties);
        const fontProperties = typeof properties.fontProperties === "string" ? this.getMapping(properties.fontProperties) : properties.fontProperties;
        const textStyle = FontUtil.createPixiTextStyle(fontProperties);
        const font = PIXI.BitmapFont.from(key, textStyle, properties);
        this.bitmapGenFonts.setValue(key, font);
    }
    _unloadBitmapGenFont(key) {
        const font = this.bitmapGenFonts.getValue(key);
        if (font) {
            PIXI.BitmapFont.uninstall(key);
            this.bitmapGenFonts.remove(key);
        }
    }
    _reloadBitmapGenFont(key) {
        const properties = this.bitmapGenMappings.getValue(key);
        const font = this.bitmapGenFonts.getValue(key);
        if (properties && !font) {
            this._addBitmapGenFont(key, properties);
        }
    }
    async _loadWebFonts(cssPaths, webFontNames) {
        await new Promise(resolve => {
            WebFont.load({
                custom: {
                    families: webFontNames,
                    urls: cssPaths
                },
                active: function () {
                    resolve();
                }
            });
        });
    }
    static getInstance() {
        if (FontManager.instance === null) {
            FontManager.instance = new FontManager();
        }
        return FontManager.instance;
    }
    static addBitmapGenFont(key, properties) {
        const fontManager = FontManager.getInstance();
        fontManager._addBitmapGenFont(key, properties);
    }
    static unloadBitmapGenFont(key) {
        const fontManager = FontManager.getInstance();
        fontManager._unloadBitmapGenFont(key);
    }
    static reloadBitmapGenFont(key) {
        const fontManager = FontManager.getInstance();
        fontManager._reloadBitmapGenFont(key);
    }
}
FontManager.instance = null;
//# sourceMappingURL=FontManager.js.map